import React, {
  createRef,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { cloneDeep } from "lodash";
import { RouteComponentProps } from "react-router";
import { RESOURCE_NAME } from "common/authorization";
import {
  FEATURE_FLAG_NAME,
  SUBMISSION_TYPE_MODULE,
  SUPPORT_EMAIL,
} from "common/constants";
import { SubmissionDefaultValuesContextV2 } from "common/services/formBuilderService/defaults";
import { arrayHasAtLeastOneItem } from "common/utils/arrays";
import {
  getInfoPanelLinkForAttached,
  getObjectDisplayForAttached,
} from "common-client/utils/customObject";
import {
  useGetPropertyForSubmissionQuery,
  useGetSisdRuleDataQuery,
  useGetSubmissionQuery,
  useRegenerateAiFieldsMutation,
  useUpdateSubmissionMutation,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { setTitle } from "../../utils/title";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { Markdown } from "../Common/__styles__/Markdown";
import { Button } from "../Common/Buttons/Button";
import { EmptyState } from "../Common/EmptyState";
import FullPageFormLayout from "../Common/FullPageFormLayout";
import FullPageLoadingScreen from "../Common/FullPageLoadingScreen";
import AccessDeniedPage, {
  ACCESS_DENIED_RESOURCE,
} from "../Guest/AccessDeniedPage";
import { LinkedTask } from "../Tasks/LinkedTask";
import {
  ButtonContainer,
  EmptyStateContainer,
  Stack,
} from "./__styles__/Submission";
import ActionButton from "./ActionButton";
import useSubmissionsChatAgent, {
  createFormUpdateHandler,
} from "./ChatAgent/SubmissionsChatAgent";
import { Content } from "./Content";
import { FormDataType } from "./Form";
import SubmissionHeader from "./SubmissionHeader";
import {
  generateUpsertErrorMessage,
  hasSummary,
  useSubmissionPrevLocation,
} from "./utils";

interface URLParams {
  submissionId: string;
}

export interface EditSubmissionProps extends RouteComponentProps<URLParams> {}

const EditSubmission = ({ match }: EditSubmissionProps) => {
  const { authorized, isPublic, account, publicUser } = useContext(AuthContext);
  const isGuest = isPublic && !publicUser;

  const { addErrorToast, addSuccessToast } = useStatusToasts();
  const [disabled, setDisabled] = useState(true);
  const [serverProvidedFormData, setServerProvidedFormData] =
    useState<FormDataType | null>(null);
  const [regeneratingPaths, setRegeneratingPaths] = useState<
    Record<string, boolean>
  >({});
  const [defaultValuesContext, setDefaultValuesContext] =
    useState<Maybe<SubmissionDefaultValuesContextV2>>(null);

  const { submissionId } = match.params;

  const { data, loading, error, refetch } = useGetSubmissionQuery({
    variables: { submissionId, isPublic, isGuest },
    fetchPolicy: "cache-and-network",
  });

  // Set defaultValuesContext from incoming and cached data to avoid loading flash
  // an onCompleted only fires on network response, not cached data
  const submissionDefaultValuesContext = data?.submission?.defaultValuesContext;
  useEffect(() => {
    if (submissionDefaultValuesContext && !defaultValuesContext) {
      setDefaultValuesContext(submissionDefaultValuesContext);
    }
  }, [submissionDefaultValuesContext, defaultValuesContext]);

  const [updateSubmission, { loading: updateLoading }] =
    useUpdateSubmissionMutation({
      onCompleted: data => {
        const updatedSubmission = data.updateSubmission;
        track("Submission updated", {
          submissionId: updatedSubmission.id,
          "Submission Category": submissionType.category,
          "Submission Type": submissionType.name,
        });
        addSuccessToast("Your submission has been updated.");

        return refetch();
      },
      onError: error => {
        const message = generateUpsertErrorMessage(error);
        addErrorToast(message);
      },
    });

  const submission = data?.submission;
  const propertyId = submission?.property?.id;
  const { data: propertyData, loading: propertyDataLoading } =
    useGetPropertyForSubmissionQuery({
      variables: { propertyId: propertyId! },
      skip: !propertyId,
    });

  const submissionTypeModules =
    submission?.submissionTypeVersion.submissionType.modules;

  const { data: sisdRuleData, loading: sisdRuleDataLoading } =
    useGetSisdRuleDataQuery({
      variables: { propertyId: propertyId! },
      skip:
        !submissionTypeModules?.includes(
          SUBMISSION_TYPE_MODULE.SUBSTANTIAL_IMPROVEMENT_SUBSTANTIAL_DAMAGE,
        ) || !propertyId,
    });

  const prevLocation = useSubmissionPrevLocation();

  const handleFormUpdate = useMemo(
    () =>
      createFormUpdateHandler({
        setServerProvidedFormData,
        setDisabled,
        addSuccessToast,
      }),
    [setDisabled, addSuccessToast],
  );

  const chatAgentConfig = useMemo(
    () => ({
      isPublic,
      submissionTypeVersionId: submission?.submissionTypeVersion?.id,
      formData: submission?.formData ?? {},
      onFormUpdate: handleFormUpdate,
    }),
    [
      isPublic,
      submission?.submissionTypeVersion?.id,
      submission?.formData,
      handleFormUpdate,
    ],
  );

  const {
    ChatToggleButton,
    ChatPanel: SidePanel,
    isChatPanelOpen: isSidePanelOpen,
  } = useSubmissionsChatAgent(chatAgentConfig);

  const [regenerateAIFields, { loading: regenerateAIFieldsLoading }] =
    useRegenerateAiFieldsMutation();

  const propertyProp = useMemo(() => {
    return {
      FIRMInfo: sisdRuleData?.FIRMInfo,
    };
  }, [sisdRuleData?.FIRMInfo]);

  if (error || !submission) {
    if (loading && !data) {
      return <FullPageLoadingScreen />;
    }
    if (isPublic) {
      return <AccessDeniedPage resource={ACCESS_DENIED_RESOURCE.RECORD} />;
    }

    return (
      <FullPageFormLayout
        subtitle={""}
        prevLocation={prevLocation}
        rightContainer={<div />}
      >
        <EmptyStateContainer>
          <EmptyState
            title="This record is unavailable"
            body={
              <>
                You may not have access to this file, or it may have been
                removed.
              </>
            }
            icon="missing-file"
            buttonLinkProps={{
              label: "Get help",
              onClick: () =>
                window.open(`https://forerunner.helpscoutdocs.com/`),
            }}
            primaryButtonProps={{
              label: "Contact us",
              onClick: () => window.open(`mailto:${SUPPORT_EMAIL}`),
            }}
          />
        </EmptyStateContainer>
      </FullPageFormLayout>
    );
  }

  if (
    (loading && !data) ||
    (propertyDataLoading && !propertyData) ||
    (sisdRuleDataLoading && !sisdRuleData) ||
    !defaultValuesContext
  ) {
    return <FullPageLoadingScreen />;
  }

  const submitFormRef = createRef<HTMLButtonElement>();

  const submissionType = submission.submissionTypeVersion.submissionType;

  if (isPublic) {
    setTitle(submissionType.name);
  }

  const canUpdateSubmission = submission.canUpdate ?? false;

  const handleSubmit = async (formData: FormDataType) => {
    await updateSubmission({
      variables: { data: { submissionId, formData } },
    });
  };

  const handleRegenerateAIFields = async (
    formData: FormDataType,
    paths: string[],
  ) => {
    setRegeneratingPaths(previousPaths => {
      const clonedPreviousPaths = cloneDeep(previousPaths);
      paths.forEach(path => {
        clonedPreviousPaths[path] = true;
      });
      return clonedPreviousPaths;
    });

    await regenerateAIFields({
      variables: { data: { submissionId, formData, paths } },
      onCompleted: data => {
        setServerProvidedFormData(data.regenerateAIFields);

        setRegeneratingPaths(previousPaths => {
          const clonedPreviousPaths = cloneDeep(previousPaths);
          paths.forEach(path => {
            clonedPreviousPaths[path] = false;
          });
          return clonedPreviousPaths;
        });
        setDisabled(false);
        addSuccessToast("AI fields regenerated");
      },
    });
  };

  const rightContainer = (
    <ButtonContainer>
      <ActionButton
        isEditView={true}
        submission={{
          ...submission,
          hasSummary: hasSummary(submission),
          category: submissionType.category,
        }}
        objectIsHiddenFromPublic={submission.property?.hiddenFromPublic}
      />
      {!isGuest && (
        <Button
          size="small"
          onPress={() => {
            submitFormRef.current!.click();
          }}
          isDisabled={!canUpdateSubmission || disabled}
          isPending={updateLoading || regenerateAIFieldsLoading}
          title="Update"
        />
      )}
      {!isPublic && ChatToggleButton}
    </ButtonContainer>
  );

  const tasks = submission.tasks;
  const canViewTasks = authorized({
    permission: "view",
    resource: RESOURCE_NAME.TASK,
  });
  const tasksFeature = account?.featureFlags.find(
    ff => ff.feature === FEATURE_FLAG_NAME.TASKS_WEB,
  );

  return (
    <FullPageFormLayout
      subtitle={submissionType.name}
      prevLocation={prevLocation}
      rightContainer={rightContainer}
      isSidePanelOpen={isSidePanelOpen}
      sidePanel={SidePanel}
      width="regular"
    >
      <Stack main>
        <SubmissionHeader
          account={{
            logoUrl: account?.logoUrl ?? "",
            name: account?.name ?? "",
          }}
          objectDisplay={getObjectDisplayForAttached(submission)}
          objectInfoPanelLink={getInfoPanelLinkForAttached({
            ...submission,
            isPublic,
          })}
          submissionType={submissionType}
          viewers={submission.viewers}
        >
          {submissionType.description && (
            <Markdown markdown={submissionType.description} />
          )}
        </SubmissionHeader>
        {tasksFeature?.isEnabled &&
          canViewTasks &&
          arrayHasAtLeastOneItem(tasks) && <LinkedTask tasks={tasks} />}
        <Content
          submission={submission}
          handleRegenerateAIFields={handleRegenerateAIFields}
          regeneratingPaths={regeneratingPaths}
          setFormDisabled={setDisabled}
          propertyProp={propertyProp}
          defaultValuesContext={defaultValuesContext}
          serverProvidedFormData={serverProvidedFormData}
          onSubmit={handleSubmit}
          account={sisdRuleData?.account}
          submitFormRef={submitFormRef}
        />
      </Stack>
    </FullPageFormLayout>
  );
};

export default EditSubmission;
